// =============================================================================
// availabilityRows.js — who is FREE in each half-day, and who is POST-CALL
// =============================================================================
// The Final Roster's FREE and POST-CALL rows, ported from the `main` branch
// (HRM) and mirrored by the Excel export (report_export_service.py), so the
// screen and the spreadsheet list the same people. Shown only when the
// department turns on "Free and post-call rows" (schedule.rosterFreeRows); ED
// and IP keep their whole-day OFF row instead.
//
// Rules, as in main:
//   * weekdays only;
//   * consultants and locums are never listed (they are not floating cover);
//   * FREE in a half-day = holds no slot that half and is not on leave for it;
//     somebody on call the night before is not free that afternoon;
//   * POST-CALL = on call the night before (not a consultant).
// One addition: somebody outside their rotation dates is never "free".
// =============================================================================

import { onLeave } from './leave';

const pick = (o, ...keys) => {
  for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null) return o[k];
  return undefined;
};

const isWeekend = (dateIso) => {
  const [y, m, d] = dateIso.split('-').map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
};

const previousIso = (dateIso) => {
  const [y, m, d] = dateIso.split('-').map(Number);
  const prev = new Date(y, m - 1, d - 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`;
};

const halfOf = (shift) => (new Date(shift.start).getHours() < 12 ? 'AM' : 'PM');

/** Never listed as cover: consultants (rank 0) and locums -- by the rank and
 *  the locum flag set on the person, never by title text (Oct 2026). */
export const isFloatingCover = (emp) => {
  if (pick(emp, 'is_locum', 'isLocum')) return false;
  return pick(emp, 'rank') !== 0;
};

const onRotation = (emp, dateIso) => {
  const start = String(pick(emp, 'active_start_date', 'activeStartDate') || '').slice(0, 10);
  const end = String(pick(emp, 'active_end_date', 'activeEndDate') || '').slice(0, 10);
  return (!start || dateIso >= start) && (!end || dateIso <= end);
};

const onCallOn = (schedule, dateIso) => {
  const roster = pick(schedule, 'onCallRoster', 'on_call_roster') || [];
  return new Set(roster
    .filter((oc) => String(oc?.date || '').slice(0, 10) === dateIso && oc.employee)
    .map((oc) => oc.employee.id));
};

/** Non-consultants on call the night before `dateIso` (weekdays only). */
export function postCallStaff(schedule, dateIso) {
  if (!schedule?.employees || isWeekend(dateIso)) return [];
  const lastNight = onCallOn(schedule, previousIso(dateIso));
  return schedule.employees
    .filter((e) => lastNight.has(e.id) && isFloatingCover(e))
    .map((e) => e.name)
    .sort((a, b) => a.localeCompare(b));
}

/** Names free in one half ('AM' | 'PM') of `dateIso` (weekdays only). */
export function freeStaff(schedule, dateIso, half) {
  if (!schedule?.employees || isWeekend(dateIso)) return [];
  const busy = new Set((schedule.shifts || [])
    .filter((s) => s.employee && String(s.start || '').slice(0, 10) === dateIso && halfOf(s) === half)
    .map((s) => s.employee.id));
  const postCall = half === 'PM' ? onCallOn(schedule, previousIso(dateIso)) : new Set();
  return schedule.employees
    .filter((e) => isFloatingCover(e) && onRotation(e, dateIso)
      && !busy.has(e.id) && !postCall.has(e.id) && !onLeave(e, dateIso, half))
    .map((e) => e.name)
    .sort((a, b) => a.localeCompare(b));
}
